// @ts-strict-ignore
import { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IconName, useSnackBar } from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import apiCalls from '~/helpers/apiCalls';

import useWorkflowProps from './useWorkflowProps';

type UseCreateOutcomeOptions = {
  outcomeName: string;
};

/**
 * createOutcome from the workflow editor does not have all the options that the typhon outcome endpoints expect
 * This object contains the expected/default properties for a workflow outcome
 * In a future state, we would want to update the workflow editor to use the new outcomes modal with all the options
 */
const standardWorkflowOutcome = {
  icon_name: IconName.CustomOutcome,
  outcome_color: 'EBEBE6',
  description: null,
  is_alert_outcome: false,
  is_application_outcome: false,
  is_investigation_outcome: false,
  is_workflow_outcome: true,
  is_case_status: false,
  is_created_outside_settings: true,
};

async function createOutcome(
  payload: UseCreateOutcomeOptions,
  typhonDomain: string,
) {
  const newOutcome = { ...standardWorkflowOutcome, outcome: payload.outcomeName };
  return apiCalls.settings.createOutcome({
    typhonDomain,
    outcome: newOutcome,
  });
}

export function useCreateOutcome() {
  const queryClient = useQueryClient();
  const addSnackBar = useSnackBar();
  const { typhonDomain } = useContext(UserContext);

  return useMutation({
    mutationFn: (payload: UseCreateOutcomeOptions) => createOutcome(payload, typhonDomain),
    /**
     * returning the result of .invalidateQueries will keep the status of this mutation as
     * "loading" (`isLoading` would be `true) until the invalidated query is updated
     */
    onSuccess: () => queryClient.invalidateQueries(
      useWorkflowProps.getQueryKey(),
    ),
    onError: ({ responseError }) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? 'Unexpected Error creating new outcome',
        type: 'danger',
      });
    },
  });
}
